import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';
import Footer from './Footer';
import JobCard from './JobCard';
import { useSeo } from './SeoLanding';

/* /jobs/:slug  (location and category pages served by SlugResolverController)
   Same data as before: jobs, popular employers and FAQs from /slugs/{slug}.
   Added: page title, description, canonical, noindex when empty, breadcrumb and
   FAQ structured data, shared Footer, and the same JobCard used elsewhere. */

const SITE = process.env.REACT_APP_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
const resolveLogoSrc = (logo) => (!logo ? null : logo.startsWith('http') ? logo : `${BACKEND_URL}/storage/${logo}`);

function SlugPage() {
  const { id: slug } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [user] = useState(() => {
    const s = localStorage.getItem('user');
    return s ? JSON.parse(s) : null;
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setResult(null);
    api.get(`/slugs/${slug}`)
      .then((res) => { if (!cancelled) setResult(res.data); })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const isLocation = result?.type === 'location';
  const name = result?.name || '';
  const jobs = result?.jobs || [];
  const heading = isLocation ? `Jobs in ${name}` : `${name} Jobs`;
  const canonical = `${SITE}/jobs/${slug}`;

  // Hooks must run on every render, so the SEO values are safe when result is still null
  useSeo({
    title: result
      ? (isLocation ? `Jobs in ${name} – Latest Vacancies | PlanB Jobs` : `${name} Jobs in India – Apply Online | PlanB Jobs`)
      : 'Jobs | PlanB Jobs',
    description: result
      ? `Find ${jobs.length} open ${isLocation ? `jobs in ${name}` : `${name} jobs`} on PlanB Jobs. Browse vacancies, popular employers and answers to common questions.`
      : 'Browse jobs on PlanB Jobs.',
    canonical,
    noindex: notFound || (!!result && jobs.length === 0),
    jsonLd: result ? [
      {
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
          { '@type': 'ListItem', position: 2, name: 'Jobs', item: `${SITE}/jobs` },
          { '@type': 'ListItem', position: 3, name, item: canonical },
        ],
      },
      ...((result.faqs || []).length ? [{
        '@context': 'https://schema.org', '@type': 'FAQPage',
        mainEntity: result.faqs.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
      }] : []),
    ] : [],
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF5FF]">
        <Navbar user={user} />
        <div className="p-10 text-center text-gray-500">Loading...</div>
      </div>
    );
  }

  if (notFound || !result) {
    return (
      <div className="min-h-screen bg-[#FAF5FF]">
        <Navbar user={user} />
        <div className="max-w-xl mx-auto px-4 py-16 text-center">
          <p className="font-bold text-lg mb-2">We could not find that page.</p>
          <p className="text-sm text-gray-500 mb-6">The location or category may have changed. Search all jobs instead.</p>
          <Link to="/jobs" className="inline-block bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-6 py-2.5 rounded-xl text-sm font-semibold">Explore all jobs</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF5FF]">
      <Navbar user={user} />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <nav aria-label="Breadcrumb" className="text-xs text-gray-500 mb-4">
          <ol className="flex flex-wrap items-center gap-1.5">
            <li><Link to="/" className="hover:text-[#7C3AED]">Home</Link></li>
            <li aria-hidden="true">/</li>
            <li><Link to="/jobs" className="hover:text-[#7C3AED]">Jobs</Link></li>
            <li aria-hidden="true">/</li>
            <li className="font-semibold text-gray-700" aria-current="page">{name}</li>
          </ol>
        </nav>

        <h1 className="inline-block text-2xl sm:text-4xl font-bold tracking-tight bg-gradient-to-r from-[#7C3AED] to-[#DB2777] bg-clip-text text-transparent mb-2">
          {heading}
        </h1>
        <p className="text-sm text-gray-500 mb-8">
          Browse {jobs.length} open position{jobs.length === 1 ? '' : 's'} and apply online.
        </p>

        <h2 className="text-lg font-semibold text-gray-800 mb-3">Latest Jobs</h2>
        {jobs.length === 0 ? (
          <div className="bg-white rounded-xl border border-purple-100 p-8 text-center mb-8">
            <p className="text-gray-600 mb-4">No open vacancies here right now.</p>
            <Link to="/jobs" className="inline-block bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-6 py-2.5 rounded-lg text-sm font-semibold">Explore all jobs</Link>
          </div>
        ) : (
          <div className="space-y-4 mb-8">
            {jobs.map((job) => (
              <JobCard
                key={job.id} job={job} applied={false} applying={false}
                logoSrc={resolveLogoSrc(job.company_logo || (job.employer && job.employer.company_logo))}
                onApply={() => navigate(`/jobs/${job.id}`)}
              />
            ))}
          </div>
        )}

        {result.popular_employers?.length > 0 && (
          <>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Popular Employers</h2>
            <div className="flex flex-wrap gap-2">
              {result.popular_employers.map((emp) => (
                emp.company_slug ? (
                  <button
                    key={emp.company_slug}
                    onClick={() => navigate(`/company/${emp.company_slug}`)}
                    className="text-sm font-medium px-3 py-1.5 rounded-full bg-white border border-gray-200 hover:border-[#7C3AED]"
                  >
                    {emp.company_name} ({emp.job_count})
                  </button>
                ) : (
                  <span key={emp.company_name} className="text-sm font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-500">
                    {emp.company_name} ({emp.job_count})
                  </span>
                )
              ))}
            </div>
          </>
        )}

        {result.faqs?.length > 0 && (
          <>
            <h2 className="text-lg font-semibold text-gray-800 mb-3 mt-8">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {result.faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="font-semibold text-gray-800 text-sm mb-1">{faq.question}</p>
                  <p className="text-sm text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default SlugPage;